import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, AUTH_STORAGE_KEY } from '../lib/supabase';

function getOrgSlugFromContext(): string | null {
  try {
    const slug = localStorage.getItem('last_org_slug');
    if (slug) return slug;
    const deviceContext = localStorage.getItem('device_context');
    if (deviceContext) {
      const parsed = JSON.parse(deviceContext);
      return parsed.organizationSlug || null;
    }
  } catch {}
  return null;
}

type DebugAuthState = {
  checked: boolean;
  hasSession: boolean;
  userId?: string;
  email?: string;
  storageKeyPresent?: boolean;
  error?: string;
};

export function SignInPrompt({ returnTo }: { tenantId?: string; returnTo: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const debugEnabled = searchParams.get('debugAuth') === '1';

  const [debug, setDebug] = useState<DebugAuthState>({
    checked: false,
    hasSession: false,
  });

  useEffect(() => {
    if (!debugEnabled) return;

    let cancelled = false;
    (async () => {
      try {
        const keyPresent = localStorage.getItem(AUTH_STORAGE_KEY) !== null;
        const { data, error } = await supabase.auth.getSession();
        const session = data.session;

        if (cancelled) return;
        setDebug({
          checked: true,
          hasSession: !!session?.user,
          userId: session?.user?.id,
          email: session?.user?.email ?? undefined,
          storageKeyPresent: keyPresent,
          error: error?.message,
        });
      } catch (e: any) {
        if (cancelled) return;
        setDebug({
          checked: true,
          hasSession: false,
          error: e?.message || 'unknown_error',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debugEnabled]);

  const handleLoginClick = () => {
    try {
      sessionStorage.removeItem('pwa-just-logged-out');
    } catch {}

    // Try to find orgSlug from multiple sources
    let orgSlug = getOrgSlugFromContext();

    if (!orgSlug) {
      // Fallback: extract from returnTo URL
      const match = returnTo.match(/\/([^\/]+)(?:\/|$)/);
      if (match && !['home', 'shop', 'account', 'cart', 'payment', 'checkout'].includes(match[1])) {
        orgSlug = match[1];
      }
    }

    if (!orgSlug) {
      // Fallback: localStorage
      orgSlug = localStorage.getItem('last_org_slug');
    }

    if (orgSlug) {
      console.log('[SignInPrompt] Navigating to OrgLandingPage:', orgSlug);
      navigate(`/${orgSlug}`);
    } else {
      // Last fallback: root landing page
      console.log('[SignInPrompt] No valid orgSlug, going to root');
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-card-foreground">Logga in krävs</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Fortsätt till autentisering för att öppna butiken.
        </p>

        {debugEnabled && (
          <div className="mt-4 rounded-lg border border-border bg-background/40 p-3 text-left">
            <div className="text-xs font-medium text-card-foreground">Auth debug (debugAuth=1)</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {debug.checked ? (
                <ul className="space-y-1">
                  <li>hasSession: {String(debug.hasSession)}</li>
                  <li>userId: {debug.userId ? `${debug.userId.slice(0, 8)}…` : '—'}</li>
                  <li>email: {debug.email ?? '—'}</li>
                  <li>storageKey: {AUTH_STORAGE_KEY}</li>
                  <li>storageKeyPresent: {String(debug.storageKeyPresent)}</li>
                  <li>error: {debug.error ?? '—'}</li>
                </ul>
              ) : (
                <div>Checking session…</div>
              )}
            </div>
          </div>
        )}

        <button
          className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:opacity-90"
          onClick={handleLoginClick}
        >
          Logga in med BankID
        </button>
      </div>
    </div>
  );
}
